import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  Stack,
  Paper,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  Receipt as ReceiptIcon,
  AddCircle as AddCircleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  VpnKey as VpnKeyIcon,
  Settings as SettingsIcon,
  FamilyRestroom as FamilyRestroomIcon,
} from '@mui/icons-material';
import { Client } from '../types';
import PasswordManagementModal from './PasswordManagementModal';
import FamilyMembersModal from './FamilyMembersModal';

interface ClientHistorique {
  demarches: any[];
  reparations: any[];
  solutions: any[];
  factures: any[];
}

interface EspaceClientAuth {
  username: string;
  temp_password: string | null;
  password_changed: boolean;
  is_active: boolean;
  last_login: string | null;
}

interface ClientDetailDrawerProps {
  open: boolean;
  client: Client | null;
  historique: ClientHistorique | null;
  espaceClientAuth: EspaceClientAuth | null;
  onClose: () => void;
  onEdit: (client: Client) => void;
  onDelete: (id: number) => void;
}

const ClientDetailDrawer: React.FC<ClientDetailDrawerProps> = ({
  open,
  client,
  historique,
  espaceClientAuth,
  onClose,
  onEdit,
  onDelete,
}) => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [familyModalOpen, setFamilyModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  if (!client) return null;

  // Vérifier si l'utilisateur est un cyber (pas admin)
  const isCyberUser = user?.role === 'cyber';

  // Calculer les démarches en cours
  const demarchesEnCours = historique?.demarches.filter(
    (d) => d.statut !== 'termine' && d.statut !== 'annule'
  ) || [];

  // Calculer les factures impayées et le montant total dû
  const facturesImpayees = historique?.factures.filter(
    (f) => f.statut_paiement === 'impaye' || f.statut_paiement === 'partiellement_paye'
  ) || [];

  const montantDu = facturesImpayees.reduce((sum, f) => sum + parseFloat(f.montant_restant || 0), 0);

  const getClientName = () => {
    if (client.type_client === 'entreprise') {
      return client.raison_sociale;
    }
    return `${client.nom} ${client.prenom || ''}`.trim();
  };

  const handleEdit = () => {
    onEdit(client);
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    setDeleteDialogOpen(false);
    onDelete(client.id);
    onClose();
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
  };

  return (
    <>
      {/* Side Panel - Client Details */}
      <Box
        sx={{
          width: 480,
          minWidth: 480,
          height: 'fit-content',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 3,
          borderRadius: 2,
          backgroundColor: 'white',
          overflow: 'hidden',
          animation: 'slideIn 0.3s ease-out',
          '@keyframes slideIn': {
            from: {
              opacity: 0,
              transform: 'translateX(20px)',
            },
            to: {
              opacity: 1,
              transform: 'translateX(0)',
            },
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 120px)', overflow: 'auto' }}>
          {/* Header */}
          <Box
            sx={{
              p: 2.5,
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              color: 'white',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', mb: 2 }}>
              <IconButton onClick={onClose} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: 'rgba(255, 255, 255, 0.3)',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                }}
              >
                {client.type_client === 'entreprise' ? (
                  <BusinessIcon sx={{ fontSize: 36 }} />
                ) : (
                  <PersonIcon sx={{ fontSize: 36 }} />
                )}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                    {getClientName()}
                  </Typography>

                  {/* Icônes d'actions */}
                  <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                    <Tooltip title="Gestion des mots de passe" arrow>
                      <IconButton
                        size="small"
                        onClick={() => setPasswordModalOpen(true)}
                        sx={{
                          color: 'white',
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.3)',
                          },
                          width: 32,
                          height: 32,
                        }}
                      >
                        <VpnKeyIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>

                    {!isCyberUser && (
                      <Tooltip title="Gestion de la famille" arrow>
                        <IconButton
                          size="small"
                          onClick={() => setFamilyModalOpen(true)}
                          sx={{
                            color: 'white',
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              bgcolor: 'rgba(255, 255, 255, 0.3)',
                            },
                            width: 32,
                            height: 32,
                            position: 'relative',
                          }}
                        >
                          <FamilyRestroomIcon sx={{ fontSize: 18 }} />
                          {client.has_family && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: -2,
                                right: -2,
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: '#4caf50',
                                border: '2px solid white',
                              }}
                            />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                <Chip
                  label={client.type_client === 'entreprise' ? 'Entreprise' : 'Particulier'}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontWeight: 500,
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
            {/* Alertes en cours */}
            {(demarchesEnCours.length > 0 || facturesImpayees.length > 0) && (
              <Stack spacing={1.5} sx={{ mb: 2 }}>
                {demarchesEnCours.length > 0 && (
                  <Alert
                    severity="info"
                    icon={<DescriptionIcon />}
                    sx={{ fontWeight: 500 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {demarchesEnCours.length} démarche{demarchesEnCours.length > 1 ? 's' : ''} en cours
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {demarchesEnCours.slice(0, 2).map((d) => (
                        <Typography key={d.id} variant="caption" display="block" color="text.secondary">
                          • {d.titre}
                        </Typography>
                      ))}
                      {demarchesEnCours.length > 2 && (
                        <Typography variant="caption" color="text.secondary">
                          + {demarchesEnCours.length - 2} autre{demarchesEnCours.length > 3 ? 's' : ''}
                        </Typography>
                      )}
                    </Box>
                  </Alert>
                )}

                {facturesImpayees.length > 0 && (
                  <Alert
                    severity="warning"
                    icon={<WarningIcon />}
                    sx={{ fontWeight: 500 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Montant dû : {montantDu.toFixed(2)} €
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {facturesImpayees.length} facture{facturesImpayees.length > 1 ? 's' : ''} impayée{facturesImpayees.length > 1 ? 's' : ''}
                    </Typography>
                  </Alert>
                )}
              </Stack>
            )}

            {/* Pas de problème */}
            {demarchesEnCours.length === 0 && facturesImpayees.length === 0 && (
              <Alert
                severity="success"
                icon={<CheckCircleIcon />}
                sx={{ mb: 2, fontWeight: 500 }}
              >
                <Typography variant="body2">
                  Aucune démarche en cours • Aucun impayé
                </Typography>
              </Alert>
            )}

            {/* Contact Info */}
            <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                INFORMATIONS DE CONTACT
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={1.5}>
                {client.telephone_principal && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PhoneIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Téléphone principal
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {client.telephone_principal}
                        {client.est_numero_etranger && (
                          <Chip label="International" size="small" sx={{ ml: 1, height: 20 }} />
                        )}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {client.telephone_secondaire && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PhoneIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Téléphone secondaire
                      </Typography>
                      <Typography variant="body1">{client.telephone_secondaire}</Typography>
                    </Box>
                  </Box>
                )}

                {client.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <EmailIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Email
                      </Typography>
                      <Typography variant="body1">{client.email}</Typography>
                    </Box>
                  </Box>
                )}

                {(client.adresse || client.ville || client.pays) && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <LocationIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Adresse
                      </Typography>
                      <Typography variant="body1">
                        {client.adresse && <>{client.adresse}<br /></>}
                        {client.ville && <>{client.ville}</>}
                        {client.pays && <>, {client.pays}</>}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Stack>
            </Paper>

            {/* Identity Documents */}
            {(client.numero_cni || client.numero_passeport || client.numero_carte_consulaire) && (
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                  DOCUMENTS D'IDENTITÉ
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <List dense disablePadding>
                  {client.numero_cni && (
                    <ListItem disablePadding sx={{ mb: 1 }}>
                      <ListItemText
                        primary={<Typography variant="body2" color="text.secondary">CNI</Typography>}
                        secondary={
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>{client.numero_cni}</Typography>
                            {client.date_expiration_cni && (
                              <Typography variant="caption" color="text.secondary">
                                Expire le: {new Date(client.date_expiration_cni).toLocaleDateString('fr-FR')}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  )}

                  {client.numero_passeport && (
                    <ListItem disablePadding sx={{ mb: 1 }}>
                      <ListItemText
                        primary={<Typography variant="body2" color="text.secondary">Passeport</Typography>}
                        secondary={
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>{client.numero_passeport}</Typography>
                            {client.date_expiration_passeport && (
                              <Typography variant="caption" color="text.secondary">
                                Expire le: {new Date(client.date_expiration_passeport).toLocaleDateString('fr-FR')}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  )}

                  {client.numero_carte_consulaire && (
                    <ListItem disablePadding>
                      <ListItemText
                        primary={<Typography variant="body2" color="text.secondary">Carte consulaire</Typography>}
                        secondary={
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>{client.numero_carte_consulaire}</Typography>
                            {client.date_expiration_carte_consulaire && (
                              <Typography variant="caption" color="text.secondary">
                                Expire le: {new Date(client.date_expiration_carte_consulaire).toLocaleDateString('fr-FR')}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  )}
                </List>
              </Paper>
            )}

            {/* Notes */}
            {client.notes && (
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                  NOTES
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2">{client.notes}</Typography>
              </Paper>
            )}
          </Box>

          {/* Footer Actions */}
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                fullWidth
                onClick={handleEdit}
              >
                Modifier
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
              >
                Supprimer
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>

      {/* Actions Panel - Always visible on the right */}
      <Box
        sx={{
          width: 280,
          minWidth: 280,
          height: 'fit-content',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 3,
          borderRadius: 2,
          backgroundColor: 'white',
          overflow: 'hidden',
          animation: 'slideIn 0.3s ease-out 0.1s',
          animationFillMode: 'both',
          '@keyframes slideIn': {
            from: {
              opacity: 0,
              transform: 'translateX(20px)',
            },
            to: {
              opacity: 1,
              transform: 'translateX(0)',
            },
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Actions rapides
            </Typography>
          </Box>

          <Stack spacing={1.5}>
            {!isCyberUser && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<DescriptionIcon />}
                  fullWidth
                  size="small"
                  sx={{ justifyContent: 'flex-start', py: 1 }}
                >
                  Nouvelle démarche
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<ReceiptIcon />}
                  fullWidth
                  size="small"
                  sx={{ justifyContent: 'flex-start', py: 1 }}
                >
                  Créer une facture
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<AddCircleIcon />}
                  fullWidth
                  size="small"
                  sx={{ justifyContent: 'flex-start', py: 1 }}
                >
                  Ajouter un paiement
                </Button>

                <Divider sx={{ my: 1.5 }} />
              </>
            )}

            <Button
              variant="contained"
              startIcon={<PersonIcon />}
              fullWidth
              size="small"
              onClick={() => navigate(`/clients/${client.id}`)}
              sx={{ justifyContent: 'flex-start', py: 1 }}
            >
              Voir détails du client
            </Button>

            <Divider sx={{ my: 1.5 }} />

            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
              HISTORIQUE
            </Typography>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary" align="center">
                Aucune activité récente
              </Typography>
            </Paper>
          </Stack>
        </Box>
      </Box>

      {/* Password Management Modal - toujours disponible */}
      <PasswordManagementModal
        open={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        clientId={client.id}
        clientName={client.type_client === 'entreprise' ? client.raison_sociale || '' : `${client.nom} ${client.prenom || ''}`}
      />

      {/* Family Members Modal */}
      <FamilyMembersModal
        open={familyModalOpen}
        onClose={() => setFamilyModalOpen(false)}
        clientId={client.id}
        clientName={client.type_client === 'entreprise' ? client.raison_sociale || '' : `${client.nom} ${client.prenom || ''}`}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon />
          Confirmer la suppression
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer définitivement le client{' '}
            <strong>{getClientName()}</strong> ?
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Cette action est irréversible et supprimera :
            </Typography>
            <Typography variant="body2" component="div">
              • Toutes les démarches terminées/annulées<br />
              • Toutes les factures payées<br />
              • Tous les devis et réparations<br />
              • Tous les mots de passe enregistrés<br />
              • Tous les membres de famille liés<br />
              • L'accès à l'espace client PWA
            </Typography>
          </Alert>
          {demarchesEnCours.length > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              ⚠️ Le client a {demarchesEnCours.length} démarche(s) en cours. La suppression sera bloquée.
            </Alert>
          )}
          {facturesImpayees.length > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              ⚠️ Le client a {facturesImpayees.length} facture(s) impayée(s) ({montantDu.toFixed(2)} €). La suppression sera bloquée.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCancelDelete} variant="outlined">
            Annuler
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={demarchesEnCours.length > 0 || facturesImpayees.length > 0}
          >
            Supprimer définitivement
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClientDetailDrawer;
